<template>
  <div
    :class="['order-card', orderClasses, { 'overdue-order': isOverdue, 'is-scheduled': isScheduledOrder, 'mealwave-online-order': isMealWaveOnlineOrder && !isUberEatsOrder, 'ubereats-order': isUberEatsOrder }]"
    :data-order-id="order.id"
    :data-mealwave="isMealWaveOnlineOrder"
    :style="familyColorStyle"
    @click="$emit('viewDetails', order)"
  >
    <!-- Status Indicator Bar -->
    <div class="status-bar" :class="statusBarClass"></div>

    <!-- Card Content Wrapper -->
    <div class="card-content">
      <!-- Card Header -->
      <div class="card-header">
      <div class="header-left">
        <!-- MealWave Online Order Badge with Customer Name -->
        <div v-if="isMealWaveOnlineOrder && !isUberEatsOrder" class="mealwave-badge">
          <i :class="order.type === 'delivery' ? 'fas fa-motorcycle' : 'fas fa-shopping-bag'"></i>
          <span>{{ order.customer?.name || 'Online' }}</span>
          <!-- Info Button -->
          <button @click.stop="openCustomerInfoModal" class="info-btn">
            <i class="fas fa-info-circle"></i>
          </button>
        </div>

        <div class="order-number-section">
          <span class="order-number">{{ orderDisplayTitle }}</span>
          <!-- New Order Badge (inline) -->
          <span v-if="isVeryNew && !isScheduledOrder" class="new-badge-inline">NEW</span>
        </div>
        <div v-if="!isTableTechOrder" class="order-subtitle">
          <template v-if="isUberEatsOrder && order.uberDisplayId">Uber #{{ order.uberDisplayId }}</template>
          <template v-else>{{ $t('orderNumber') }} {{ formatOrderNumber(order) }}</template>
        </div>
      </div>

      <div class="time-section">
        <!-- Scheduled Order: Show scheduled time (not for Uber orders) -->
        <template v-if="isScheduledOrder && !isUberEatsOrder">
          <span class="scheduled-time">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {{ scheduledTimeDisplay }}
          </span>
          <span class="order-time">Vorbestellt</span>
        </template>
        <!-- Regular Order: Show elapsed time -->
        <template v-else>
          <span :class="['elapsed-time', { 'urgent': elapsedMinutes >= 30 }]">
            <i class="fas fa-clock rotating-clock"></i>
            <span class="time-number">{{ elapsedTime }}</span>
            <i v-if="elapsedMinutes >= 30" class="fas fa-exclamation-triangle warning-icon"></i>
          </span>
          <span class="order-time">{{ formatTime(order.timestamp) }}</span>
        </template>
      </div>
    </div>

    <!-- Items List -->
    <div class="items-list">
      <div
        v-for="(item, index) in itemsToShow"
        :key="index"
        class="order-item"
        :class="{ 'is-cancelled': item.cancelled || item.deleted }"
      >
        <span class="item-qty">{{ item.quantity || 1 }}x</span>
        <div class="item-details">
          <div class="item-name-wrapper">
            <div class="item-name-row">
              <span class="item-name" :class="{'line-through': item.cancelled || item.deleted}">
                {{ getItemName(item) }}
                <span v-if="showArticleNumber && item.articleNumber && !isMealWaveOnlineOrder" class="article-number">#{{ item.articleNumber }}</span>
                <span v-if="item.destination && !isMealWaveOnlineOrder" class="destination-badge">{{ item.destination }}</span>
                <span v-if="item.isFromAddition" class="addition-dot" title="من الإضافة"></span>
              </span>
              <span v-if="item.deleted" class="deleted-badge">GELÖSCHT</span>
            </div>
            <div v-if="item.deleted && item.deletedBy" class="deleted-info">
              Gelöscht von: {{ item.deletedBy }} • {{ item.deletedAt ? formatTime(item.deletedAt) : '' }}
            </div>
          </div>

          <!-- Notes with Translation -->
          <div v-if="item.notes" class="item-notes">
            <div class="notes-content">
              <span class="notes-icon">※</span>
              <span :class="{'line-through': item.cancelled || item.deleted}">
                {{ showOriginalNote ? item.notes : (item.notesArabic || order.notesArabic || item.notes) }}
              </span>
            </div>
            <div class="notes-actions">
              <button
                v-if="hasLatinChars(item.notes) && language === 'ar' && !item.notesArabic"
                @click.stop="translateNote(item.notes, index, item)"
                class="translate-btn"
                :disabled="isTranslating"
                title="ترجمة إلى العربية"
              >
                <i class="fas fa-language" :class="{ 'fa-spin': isTranslating }"></i>
              </button>
              <button
                v-if="item.notesArabic && hasLatinChars(item.notes) && language === 'ar'"
                @click.stop="showOriginalNote = !showOriginalNote"
                class="toggle-note-btn"
                :title="showOriginalNote ? 'عرض الترجمة' : 'عرض النص الأصلي'"
              >
                <i class="fas" :class="showOriginalNote ? 'fa-globe' : 'fa-font'"></i>
              </button>
            </div>
          </div>

          <!-- MealWave Customizations -->
          <div v-if="item.customization" class="item-customizations">
            <!-- Type/Variant -->
            <div v-if="item.customization.Type" class="customization type-badge">
              <i class="fas fa-tag"></i> {{ item.customization.Type }}
            </div>
            <!-- Size -->
            <div v-if="item.customization.Größe || item.customization['Größe ']" class="customization size">
              <i class="fas fa-ruler"></i> {{ item.customization.Größe || item.customization['Größe '] }}
            </div>
            <!-- Added Addons - Each on its own line -->
            <div v-for="(addon, addonIdx) in (item.customization.addedAddons || [])" :key="'addon-'+addonIdx" class="customization addon">
              <i class="fas fa-plus"></i> {{ addon.name }}<span v-if="addon.quantity > 1"> ({{ addon.quantity }}x)</span>
              <span v-if="addon.price > 0" class="addon-price">€{{ addon.price.toFixed(2) }}</span>
            </div>
            <!-- Removed Ingredients - Each on its own line -->
            <div v-for="(ing, idx) in getRemovedIngredients(item)" :key="'removed-'+idx" class="customization removed">
              <i class="fas fa-minus"></i> {{ ing }}
            </div>
          </div>

          <!-- Ingredient Modifications (Table Tech style) -->
          <div v-if="item.ingredients && item.ingredients.length > 0" class="item-modifications">
            <span
              v-for="(ing, idx) in item.ingredients.filter(i => i.isBase && !i.included)"
              :key="'removed-' + idx"
              class="mod removed"
            >
              Ohne {{ language === 'ar' ? ing.name_ar : ing.name }}
            </span>
            <span
              v-for="(ing, idx) in item.ingredients.filter(i => (i.isBase && i.included && i.quantity > 1) || (!i.isBase && i.included && i.quantity >= 1))"
              :key="'extra-' + idx"
              class="mod added"
            >
              <template v-if="ing.quantity === 1 && !ing.isBase">+ {{ language === 'ar' ? ing.name_ar : ing.name }}</template>
              <template v-else-if="ing.quantity >= 2">Extra {{ language === 'ar' ? ing.name_ar : ing.name }}</template>
            </span>
          </div>

          <!-- Added Item Timestamp -->
          <div v-if="item.addedAt" class="added-timestamp">
            <i class="fas fa-plus-circle"></i> أضيف {{ formatTime(item.addedAt) }}
          </div>

          <!-- Cancelled Item Info -->
          <div v-if="item.cancelled" class="cancelled-info">
            <i class="fas fa-ban"></i> {{ $t('cancelled') }} {{ item.cancellationTimestamp ? formatTime(item.cancellationTimestamp) : '' }}
          </div>
        </div>
      </div>

        <!-- Show more button -->
        <div v-if="hasMoreItems" class="show-more-wrapper">
          <button
            @click.stop="$emit('viewDetails', order)"
            class="show-more-btn"
          >
            +{{ filteredItems.length - 5 }} {{ language === 'ar' ? 'المزيد' : 'mehr' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Card Footer - Always at bottom -->
    <div class="card-footer">
      <div class="footer-info">
        <span class="item-count">{{ totalItems }} {{ $t('items') }}</span>
        <span class="customer-name">
          <i class="fas fa-user"></i>
          {{ order.customer ? (order.customer.name || 'Guest') : getWaiterName(order.waiter) }}
        </span>
      </div>

      <!-- Action Button -->
      <button
        v-if="orderStatus.status === 'new'"
        @click.stop="$emit('action', order.id, 'prepare')"
        class="action-btn prepare"
      >
        <i class="fas fa-fire"></i>
        {{ language === 'ar' ? 'ابدأ' : 'Start' }}
      </button>
      <!-- Ready for Pickup button - for Uber orders -->
      <button
        v-else-if="orderStatus.status === 'preparing' && isUberEatsOrder"
        @click.stop="$emit('action', order.id, 'uberReady')"
        class="action-btn uber-ready"
      >
        <i class="fas fa-motorcycle"></i>
        {{ language === 'ar' ? 'جاهز' : 'Ready' }}
      </button>
      <!-- Done button - for non-Uber orders -->
      <button
        v-else-if="orderStatus.status === 'preparing'"
        @click.stop="$emit('action', order.id, 'complete')"
        class="action-btn complete"
      >
        <i class="fas fa-check"></i>
        {{ language === 'ar' ? 'تم' : 'Done' }}
      </button>
      <div v-else-if="orderStatus.status === 'cancelled'" class="cancelled-indicator">
        <i class="fas fa-ban"></i> {{ $t('cancelled') }}
      </div>
    </div>

    <!-- Scheduled Order Badge (not for Uber orders) -->
    <div v-if="isScheduledOrder && !isUberEatsOrder" class="scheduled-badge">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
      {{ scheduledDateDisplay }}
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'OrderCard',
  props: {
    order: {
      type: Object,
      required: true
    },
    isNew: {
      type: Boolean,
      default: false
    },
    isVirtual: {
      type: Boolean,
      default: false
    },
    familyColor: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      currentTime: Date.now(),
      timerInterval: null,
      isTranslating: false,
      showOriginalNote: false,
      showCustomerInfo: false,
      uberReadyLoading: false
    };
  },
  computed: {
    ...mapState(['selectedStation', 'language', 'menu', 'showArticleNumber']),
    ...mapGetters(['getMenuItemByArticleNumber']),

    // Family color style with RGB conversion for glow effects
    familyColorStyle() {
      if (!this.familyColor) return {};

      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
          : '0, 0, 0';
      };

      return {
        '--family-color': this.familyColor,
        '--family-color-rgb': hexToRgb(this.familyColor)
      };
    },

    statusBarClass() {
      if (this.isScheduledOrder) return 'scheduled';
      if (this.isOverdue) return 'urgent';
      if (this.orderStatus.status === 'preparing') return 'preparing';
      return 'new';
    },

    // Check if this is a Table Tech order
    isTableTechOrder() {
      return this.order._source === 'tabletech';
    },

    // Check if this is a MealWave online order (delivery/pickup)
    isMealWaveOnlineOrder() {
      return this.order.isMealWaveOrder || this.order.source === 'mealwave' || this.order.source === 'ubereats';
    },

    // Check if this is an UberEats order
    isUberEatsOrder() {
      return this.order.source === 'ubereats';
    },

    // Display title: Table number for Table Tech, Order number for MealWave
    orderDisplayTitle() {
      if (this.isTableTechOrder) {
        const tableNum = this.order.tableNumber || this.order.table;
        const isTakeaway = !tableNum ||
          String(tableNum).toLowerCase() === 'takeaway' ||
          this.order.orderType === 'zum-mitnehmen' ||
          this.order.orderType === 'takeaway';

        const additionSuffix = this.order.isAddition ? ' +' : '';

        if (isTakeaway) {
          const takeawayText = this.language === 'ar' ? 'سفري' : 'Außer Haus';
          return takeawayText + additionSuffix;
        }
        return `${this.$t('table')} ${tableNum}${additionSuffix}`;
      }

      // UberEats orders - show UberEats branding
      if (this.isUberEatsOrder) {
        return 'Uber Eats';
      }

      // MealWave online orders
      if (this.isMealWaveOnlineOrder) {
        if (this.order.type === 'delivery') {
          return this.language === 'ar' ? 'توصيل' : 'Lieferung';
        }
        return this.language === 'ar' ? 'استلام' : 'Abholung';
      }

      // MealWave orders - show order number
      return this.order.orderNumber ? '#' + this.order.orderNumber : this.titleDisplay;
    },

    titleDisplay() {
      if (this.order.type === 'delivery') {
        return this.language === 'en' ? 'Delivery' : 'Lieferung';
      }
      if (this.order.type === 'pickup') {
        return this.language === 'en' ? 'Pickup' : 'Abholung';
      }
      if (!this.order.tableNumber ||
          this.order.tableNumber.toLowerCase() === 'takeaway' ||
          this.order.tableNumber === 'Takeaway' ||
          this.order.orderType === 'zum-mitnehmen' ||
          this.order.orderType === 'takeaway') {
        if (this.order.takeawayNumber) {
          return `${this.$t('takeaway')} #${this.order.takeawayNumber}`;
        }
        return this.$t('takeaway');
      }
      return `${this.$t('table')} ${this.order.tableNumber}`;
    },

    filteredItems() {
      let items = [];
      if (this.order.items && Array.isArray(this.order.items)) {
        return this.order.items;
      }
      if (!this.selectedStation?.destinationCode) {
        ['K', 'D', 'O'].forEach(section => {
          if (this.order[section]?.items) {
            items = items.concat(this.order[section].items);
          }
        });
      } else {
        const section = this.order[this.selectedStation.destinationCode];
        items = section?.items || [];
      }
      return items;
    },

    itemsToShow() {
      return this.filteredItems.slice(0, 5);
    },

    hasMoreItems() {
      return this.filteredItems.length > 5;
    },

    totalItems() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0);
    },

    orderStatus() {
      if (this.order.items && Array.isArray(this.order.items)) {
        const status = this.order.status || 'new';
        if (status === 'completed' || status === 'ready' || status === 'Ready' || status === 'Completed') {
          return { text: this.$t('statusReady'), color: 'green', status: 'completed' };
        } else if (status === 'preparing' || status === 'Preparing' || status === 'In Progress') {
          return { text: this.$t('statusPreparing'), color: 'yellow', status: 'preparing' };
        } else if (status === 'Cancelled' || status === 'cancelled') {
          return { text: this.$t('cancelled'), color: 'red', status: 'cancelled' };
        }
        return { text: this.$t('statusNew'), color: 'blue', status: 'new' };
      }
      const section = this.order[this.selectedStation?.destinationCode];
      const sectionStatus = section?.status;
      if (sectionStatus === 'Completed' || sectionStatus === 'Ready') {
        return { text: this.$t('statusReady'), color: 'green', status: 'completed' };
      } else if (sectionStatus === 'Preparing' || sectionStatus === 'In Progress') {
        return { text: this.$t('statusPreparing'), color: 'yellow', status: 'preparing' };
      } else if (sectionStatus === 'Cancelled') {
        return { text: this.$t('cancelled'), color: 'red', status: 'cancelled' };
      }
      return { text: this.$t('statusNew'), color: 'blue', status: 'new' };
    },

    orderClasses() {
      return {
        'new-order': this.isNew && !this.order.isAddition && !this.order.isMergedFamily,
        'addition-order': this.order.isAddition,
        'merged-family-order': this.order.isMergedFamily,
        'has-new-items': this.hasNewItems
      };
    },

    hasNewItems() {
      return this.filteredItems.some(item => item.addedAt);
    },

    elapsedTime() {
      if (!this.order.timestamp) return '';
      const orderTime = typeof this.order.timestamp === 'string'
        ? new Date(this.order.timestamp).getTime()
        : parseInt(this.order.timestamp);
      const elapsed = this.currentTime - orderTime;
      const minutes = Math.floor(elapsed / 60000);
      const hours = Math.floor(minutes / 60);
      if (hours > 0) {
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
      }
      return `${minutes}m`;
    },

    elapsedMinutes() {
      if (!this.order.timestamp) return 0;
      const orderTime = typeof this.order.timestamp === 'string'
        ? new Date(this.order.timestamp).getTime()
        : parseInt(this.order.timestamp);
      const elapsed = this.currentTime - orderTime;
      return Math.floor(elapsed / 60000);
    },

    isOverdue() {
      return this.elapsedMinutes >= 30 && this.orderStatus.status === 'new';
    },

    isCriticalOverdue() {
      return this.elapsedMinutes >= 60 && this.orderStatus.status === 'new';
    },

    isVeryNew() {
      return this.elapsedMinutes < 5 && !this.isScheduledOrder;
    },

    // Scheduled order detection
    isScheduledOrder() {
      return this.order.orderType === 'scheduled' || !!this.order.scheduledFor;
    },

    scheduledTimeDisplay() {
      if (!this.order.scheduledFor) return '';
      if (this.order.scheduledTime) {
        return this.order.scheduledTime;
      }
      const date = new Date(this.order.scheduledFor);
      return date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    },

    scheduledDateDisplay() {
      if (!this.order.scheduledFor) return 'Geplant';
      if (this.order.scheduledDisplay) {
        return this.order.scheduledDisplay.replace(' Uhr', '').replace(', ', ' ');
      }
      const date = new Date(this.order.scheduledFor);
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const time = date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      if (date.toDateString() === now.toDateString()) {
        return `Heute ${time}`;
      }
      if (date.toDateString() === tomorrow.toDateString()) {
        return `Morgen ${time}`;
      }
      const dayName = date.toLocaleDateString('de-DE', { weekday: 'short' });
      return `${dayName} ${time}`;
    }
  },
  methods: {
    getItemName(item) {
      const menuItem = this.getMenuItemByArticleNumber(item.articleNumber);
      if (this.language === 'ar') {
        return item.name_ar || (menuItem && menuItem.name_ar) || item.name || item.articleNumber || 'Unknown Item';
      }
      return item.name || (menuItem && menuItem.name) || item.name_ar || item.articleNumber || 'Unknown Item';
    },

    getWaiterName(waiterInfo) {
      if (!waiterInfo) return 'Unknown';
      if (typeof waiterInfo === 'object' && waiterInfo.name) return waiterInfo.name;
      if (typeof waiterInfo === 'string') return waiterInfo;
      return 'Unknown';
    },

    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = typeof timestamp === 'string'
        ? new Date(timestamp)
        : new Date(parseInt(timestamp));
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    formatOrderNumber(order) {
      if (order.orderNumber && typeof order.orderNumber === 'number') {
        return order.orderNumber;
      }
      if (order.orderNumber && typeof order.orderNumber === 'string') {
        const numericMatch = order.orderNumber.match(/\d+/);
        if (numericMatch) return numericMatch[0];
        if (order.orderNumber.length < 10) return order.orderNumber;
      }
      if (order.isAddition) {
        return order.orderNumber || 'A-' + (order.parentOrderId ? order.parentOrderId.substring(0, 4) : '');
      }
      if (order.id) {
        return '#' + order.id.substring(0, 4);
      }
      return '-';
    },

    // Check if text contains Latin or non-Arabic characters
    hasLatinChars(text) {
      if (!text) return false;
      const hasLatin = /[a-zA-Z]/.test(text);
      const hasNonArabic = /[^\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF\s\d.,!?;:()-]/u.test(text);
      return hasLatin || hasNonArabic;
    },

    // Translate note to Arabic via API
    async translateNote(note, itemIndex, item) {
      if (!note || this.isTranslating) return;

      this.isTranslating = true;

      try {
        const restaurantId = this.$store.state.selectedRestaurantId;
        const orderId = this.order.id || this.order.orderId;
        const destinationCode = this.$store.state.selectedStation?.destinationCode || item.destination;

        if (!restaurantId || !orderId) {
          console.error('Missing restaurantId or orderId for translation');
          return;
        }

        const response = await fetch('https://api.h1-software.com/api/translation/translate-note', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            note,
            restaurantId,
            orderId,
            itemIndex,
            destinationCode
          })
        });

        if (!response.ok) {
          throw new Error(`Translation failed: ${response.status}`);
        }

        const data = await response.json();
        this.$emit('translationSuccess', data.arabic);

      } catch (error) {
        console.error('Translation error:', error);
        this.$emit('translationError', error.message);
      } finally {
        this.isTranslating = false;
      }
    },

    // Get removed ingredients from MealWave customization
    getRemovedIngredients(item) {
      if (item.customization?.removedIngredients && item.customization.removedIngredients.length > 0) {
        return item.customization.removedIngredients.map(ing => ing.name);
      }
      if (!item.customization?.mainIngredientQuantities) return [];
      return [];
    },

    // Open customer info modal
    openCustomerInfoModal() {
      this.closeCustomerInfoModal();

      const order = this.order;
      const lang = this.language;

      const modalHtml = `
        <div class="customer-info-modal" id="customer-info-modal-${order.id}">
          <div class="customer-info-content">
            <div class="modal-header">
              <h3><i class="fas fa-user-circle mr-2"></i>${lang === 'ar' ? 'معلومات الطلب' : 'Bestellinformationen'}</h3>
              <button class="close-btn" id="close-customer-modal">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="modal-body">
              <div class="info-section">
                <div class="section-title">
                  <i class="fas fa-user"></i>
                  ${lang === 'ar' ? 'العميل' : 'Kunde'}
                </div>
                ${order.customer?.name ? `
                <div class="info-row">
                  <span class="label">${lang === 'ar' ? 'الاسم' : 'Name'}</span>
                  <span class="value">${order.customer.name}</span>
                </div>` : ''}
                ${order.customer?.phone ? `
                <div class="info-row">
                  <span class="label">${lang === 'ar' ? 'الهاتف' : 'Telefon'}</span>
                  <a href="tel:${order.customer.phone}" class="value phone-link">
                    <i class="fas fa-phone mr-1"></i>${order.customer.phone}
                  </a>
                </div>` : ''}
                ${order.customer?.email ? `
                <div class="info-row">
                  <span class="label">${lang === 'ar' ? 'البريد' : 'E-Mail'}</span>
                  <span class="value">${order.customer.email}</span>
                </div>` : ''}
              </div>

              ${order.type === 'delivery' && order.deliveryAddress ? `
              <div class="info-section">
                <div class="section-title">
                  <i class="fas fa-map-marker-alt"></i>
                  ${lang === 'ar' ? 'عنوان التوصيل' : 'Lieferadresse'}
                </div>
                <div class="info-row">
                  <span class="value address">${order.deliveryAddress}</span>
                </div>
              </div>` : ''}

              <div class="info-section">
                <div class="section-title">
                  <i class="fas fa-shopping-bag"></i>
                  ${lang === 'ar' ? 'نوع الطلب' : 'Bestellart'}
                </div>
                <div class="info-row">
                  <span class="label">${lang === 'ar' ? 'النوع' : 'Typ'}</span>
                  <span class="value type-badge ${order.type}">
                    <i class="${order.type === 'delivery' ? 'fas fa-motorcycle' : 'fas fa-shopping-bag'} mr-1"></i>
                    ${order.type === 'delivery' ? (lang === 'ar' ? 'توصيل' : 'Lieferung') : (lang === 'ar' ? 'استلام' : 'Abholung')}
                  </span>
                </div>
                <div class="info-row">
                  <span class="label">${lang === 'ar' ? 'الدفع' : 'Zahlung'}</span>
                  <span class="value payment-badge ${order.payment?.method || 'card'}">
                    <i class="${order.payment?.method === 'cash' ? 'fas fa-money-bill-wave' : 'fas fa-credit-card'} mr-1"></i>
                    ${order.payment?.method === 'cash' ? (lang === 'ar' ? 'نقدي' : 'Bar') : (lang === 'ar' ? 'بطاقة' : 'Karte')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      const modalContainer = document.createElement('div');
      modalContainer.innerHTML = modalHtml;
      const modal = modalContainer.firstElementChild;
      document.body.appendChild(modal);

      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closeCustomerInfoModal();
        }
      });

      const closeBtn = modal.querySelector('#close-customer-modal');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          this.closeCustomerInfoModal();
        });
      }

      this.showCustomerInfo = true;
    },

    closeCustomerInfoModal() {
      const modal = document.querySelector('.customer-info-modal');
      if (modal) {
        modal.remove();
      }
      this.showCustomerInfo = false;
    },

    // Mark Uber order as ready for pickup
    async markUberReady() {
      if (this.uberReadyLoading) return;

      this.uberReadyLoading = true;

      // Get orderId - try multiple possible fields
      const orderId = this.order.orderId || this.order.id || this.order.uberOrderId;
      // Get restaurantId - try multiple sources
      const restaurantId = this.order.restaurantId || this.$store.state.mealwaveRestaurantId || this.$store.state.connectedMealWaveRestaurantId;
      // Get storeId as fallback
      const storeId = this.order.uberStoreId || this.order.storeId;

      console.log('Marking Uber order as ready:', { orderId, restaurantId, storeId, order: this.order });

      try {
        const response = await fetch('https://api.meal-wave.com/api/uber/ready', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId,
            restaurantId,
            storeId
          })
        });

        const result = await response.json();
        console.log('Uber ready response:', result);

        if (result.success) {
          console.log('Order marked as ready - driver will be dispatched!');
          // Show success feedback
          alert(this.language === 'ar' ? '✅ تم إرسال الطلب - سيتم إرسال السائق!' : '✅ Order ready - driver will be dispatched!');
        } else {
          console.error('Failed to mark order as ready:', result.error);
          alert(this.language === 'ar' ? '❌ فشل إرسال الطلب' : '❌ Failed to mark ready: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error marking Uber order as ready:', error);
        alert(this.language === 'ar' ? '❌ خطأ في الاتصال' : '❌ Connection error');
      } finally {
        this.uberReadyLoading = false;
      }
    }
  },
  watch: {
    isCriticalOverdue(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.$emit('criticalAlert', this.order);
      }
    }
  },
  mounted() {
    this.timerInterval = setInterval(() => {
      this.currentTime = Date.now();
    }, 60000);
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.closeCustomerInfoModal();
  }
};
</script>

<style scoped>
/* ── Card ── flat, Kasse-like: 4 px corners, one hairline, status bar on top ── */
.order-card { position: relative; background: var(--ods-surface); border-radius: var(--ods-radius); overflow: hidden; cursor: pointer; transition: box-shadow .15s, border-color .15s; display: flex; flex-direction: column; border: 1px solid var(--ods-line); padding: 0; height: 100%; box-shadow: var(--ods-shadow-sm); }
.order-card:hover { box-shadow: var(--ods-shadow); border-color: var(--ods-line-2); }
.card-content { display: flex; flex-direction: column; flex: 1; }

.status-bar { height: 4px; width: 100%; }
.status-bar.new { background: var(--ods-new); }
.status-bar.preparing { background: var(--ods-prep); }
.status-bar.scheduled { background: var(--ods-sched); }
.status-bar.urgent { background: var(--ods-danger); animation: pulse-urgent 2s ease-in-out infinite; }
@keyframes pulse-urgent { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }

/* ── Header ── */
.card-header { padding: 12px 14px 10px; display: flex; justify-content: space-between; align-items: flex-start; flex-shrink: 0; border-bottom: 1px solid var(--ods-line); }
.header-left { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
.order-number-section { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.order-number { font-size: 24px; font-weight: 800; color: var(--ods-ink); letter-spacing: -.3px; line-height: 1.1; font-variant-numeric: tabular-nums; }
.order-subtitle { font-size: 12px; color: var(--ods-ink-3); font-weight: 600; }
.time-section { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
.elapsed-time { display: flex; align-items: center; gap: 5px; font-size: 13px; font-weight: 700; color: var(--ods-ink-2); line-height: 1; font-variant-numeric: tabular-nums; }
.elapsed-time.urgent { color: var(--ods-danger-ink); }
.elapsed-time .rotating-clock { font-size: 12px; animation: rotate 4s linear infinite; }
.elapsed-time .time-number { margin-top: 1px; }
.elapsed-time .warning-icon { font-size: 12px; margin-left: 2px; }
@keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.scheduled-time { display: flex; align-items: center; gap: 4px; font-size: 14px; font-weight: 700; color: var(--ods-sched-ink); }
.order-time { font-size: 11px; color: var(--ods-ink-3); font-variant-numeric: tabular-nums; }

/* ── Source badges (Uber / MealWave online) ── */
.ubereats-badge, .mealwave-badge { display: inline-flex; align-items: center; gap: 6px; padding: 3px 8px; font-size: 11px; font-weight: 800; border-radius: var(--ods-radius-sm); margin-bottom: 6px; letter-spacing: .02em; }
.ubereats-badge { background: var(--ods-uber); color: #fff; }
.ubereats-badge .uber-id { background: rgba(255,255,255,.22); padding: 1px 5px; border-radius: 2px; font-size: 10px; }
.mealwave-badge { background: var(--ods-mealwave); color: #fff; }
.info-btn { background: rgba(255,255,255,.22); border: none; border-radius: 2px; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #fff; margin-left: 2px; }
.info-btn:hover { background: rgba(255,255,255,.4); }
.info-btn i { font-size: 10px; }

/* ── Items ── */
.items-list { padding: 0 14px; flex: 1; }
.order-item { display: flex; gap: 10px; padding: 9px 0; border-bottom: 1px solid var(--ods-line); }
.order-item:last-child { border-bottom: none; }
.order-item.is-cancelled { opacity: .55; }
.item-qty { font-size: 19px; font-weight: 800; color: var(--ods-prep-ink); min-width: 34px; font-variant-numeric: tabular-nums; }
.item-details { flex: 1; min-width: 0; }
.item-name-wrapper { display: flex; flex-direction: column; gap: 2px; }
.item-name-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.item-name { font-size: 17px; font-weight: 700; color: var(--ods-ink); line-height: 1.25; }
.item-name.line-through { text-decoration: line-through; color: var(--ods-ink-3); }
.article-number { font-size: 11px; color: var(--ods-ink-3); margin-left: 4px; font-weight: 600; }
.destination-badge { font-size: 10px; background: var(--ods-info-tint); color: var(--ods-info-ink); padding: 1px 5px; border-radius: 2px; margin-left: 4px; font-weight: 700; }
.addition-dot { display: inline-block; width: 8px; height: 8px; background: var(--ods-ok); border-radius: 50%; margin-left: 4px; }
.deleted-badge { background: var(--ods-danger); color: #fff; padding: 2px 6px; font-size: 9px; font-weight: 800; border-radius: 2px; text-transform: uppercase; letter-spacing: .05em; }
.deleted-info { font-size: 10px; color: var(--ods-danger-ink); font-style: italic; }

/* Notes */
.item-notes { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 5px; gap: 8px; }
.notes-content { display: flex; gap: 5px; font-size: 13px; color: var(--ods-prep-ink); font-weight: 700; flex: 1; background: var(--ods-prep-tint); border-left: 3px solid var(--ods-prep); padding: 4px 8px; border-radius: 2px; }
.notes-icon { flex-shrink: 0; }
.notes-actions { display: flex; gap: 4px; flex-shrink: 0; }
.translate-btn, .toggle-note-btn { border: none; border-radius: var(--ods-radius-sm); width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #fff; }
.translate-btn { background: var(--ods-ok); }
.translate-btn:disabled { opacity: .6; cursor: not-allowed; }
.toggle-note-btn { background: var(--ods-info); }
.translate-btn i, .toggle-note-btn i { font-size: 11px; }

/* Customizations (MealWave) */
.item-customizations { margin-top: 8px; display: flex; flex-direction: column; gap: 4px; }
.customization { font-size: 13px; padding: 4px 8px; border-radius: var(--ods-radius-sm); font-weight: 700; display: flex; align-items: center; gap: 8px; }
.customization i { font-size: 11px; opacity: .85; }
.customization .addon-price { margin-left: auto; font-size: 12px; opacity: .75; }
.customization.type-badge { background: var(--ods-surface-2); color: var(--ods-ink-2); }
.customization.size { background: var(--ods-info-tint); color: var(--ods-info-ink); }
.customization.addon { background: var(--ods-ok-tint); color: var(--ods-ok-ink); }
.customization.removed { background: var(--ods-danger-tint); color: var(--ods-danger-ink); }

/* Ingredient modifications (Table Tech) */
.item-modifications { margin-top: 5px; display: flex; flex-wrap: wrap; gap: 5px; }
.mod { font-size: 11px; padding: 2px 7px; border-radius: 2px; font-weight: 700; }
.mod.removed { background: var(--ods-danger-tint); color: var(--ods-danger-ink); }
.mod.added { background: var(--ods-ok-tint); color: var(--ods-ok-ink); }
.added-timestamp { margin-top: 5px; font-size: 11px; color: var(--ods-ok-ink); font-weight: 600; }
.added-timestamp i { margin-right: 4px; }
.cancelled-info { margin-top: 5px; font-size: 11px; color: var(--ods-danger-ink); font-weight: 600; }
.cancelled-info i { margin-right: 4px; }

.show-more-wrapper { display: flex; justify-content: center; margin: 8px 0 4px; }
.show-more-btn { padding: 5px 12px; background: var(--ods-surface-2); border: 1px solid var(--ods-line-2); border-radius: var(--ods-radius-sm); color: var(--ods-ink-2); font-size: 12px; font-weight: 700; cursor: pointer; }
.show-more-btn:hover { background: var(--ods-surface-3); color: var(--ods-ink); }

/* ── Footer ── */
.card-footer { padding: 10px 14px 12px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--ods-line); flex-shrink: 0; background: var(--ods-surface-2); gap: 10px; }
.footer-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.item-count { font-size: 12px; color: var(--ods-ink-3); font-weight: 600; }
.customer-name { font-size: 13px; color: var(--ods-ink); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.customer-name i { margin-right: 4px; font-size: 11px; color: var(--ods-ink-3); }

/* Action buttons — big, flat, high contrast (like BAR / KARTE on the Kasse) */
.action-btn { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 44px; padding: 0 28px; border-radius: var(--ods-radius); font-size: 15px; font-weight: 800; border: none; cursor: pointer; transition: filter .15s, transform .05s; color: #fff; }
.action-btn i { font-size: 13px; }
.action-btn:hover { filter: brightness(1.08); }
.action-btn:active { transform: translateY(1px); }
.action-btn.prepare { background: var(--ods-prep); }
.action-btn.complete { background: var(--ods-ok); }
.action-btn.uber-ready { background: var(--ods-uber); font-size: 13px; padding: 0 16px; }
.action-btn.uber-ready:disabled { opacity: .7; cursor: not-allowed; }
.cancelled-indicator { background: var(--ods-danger-tint); color: var(--ods-danger-ink); padding: 8px 14px; border-radius: var(--ods-radius); font-size: 13px; font-weight: 700; }

/* NEW badge */
.new-badge-inline { display: inline-flex; align-items: center; justify-content: center; background: var(--ods-ok); color: #fff; font-size: 9px; font-weight: 800; padding: 3px 6px; border-radius: 2px; letter-spacing: .08em; text-transform: uppercase; animation: new-badge-blink 1.6s ease-in-out infinite; }
@keyframes new-badge-blink { 0%, 100% { opacity: 1; } 50% { opacity: .55; } }
.scheduled-badge { position: absolute; top: 8px; right: 8px; background: var(--ods-sched); color: #fff; font-size: 10px; font-weight: 800; padding: 3px 7px; border-radius: 2px; display: flex; align-items: center; gap: 4px; }

/* ── States (border colour carries the state; no glows) ── */
.order-card.overdue-order { border-color: var(--ods-danger) !important; box-shadow: 0 0 0 1px var(--ods-danger); animation: pulseRed 2s ease-in-out infinite; }
@keyframes pulseRed { 0%, 100% { box-shadow: 0 0 0 1px var(--ods-danger); } 50% { box-shadow: 0 0 0 3px var(--ods-danger-tint); } }
.order-card.is-scheduled { border-color: var(--ods-sched); }
.order-card.new-order { animation: fadeInOut 1.5s infinite; border-color: var(--ods-ok); }
.order-card.addition-order { border-color: var(--ods-prep); }
.order-card.merged-family-order { border: 2px solid var(--ods-ok) !important; }
.order-card.has-new-items { animation: fadeInOut 1.5s infinite; border-color: var(--ods-prep); box-shadow: 0 0 0 1px var(--ods-prep); }
@keyframes fadeInOut { 0% { opacity: 1; } 50% { opacity: .85; } 100% { opacity: 1; } }
.order-card[style*="--family-color"] { border: 2px solid var(--family-color); }
.order-card.addition-order[style*="--family-color"] { border-style: dashed; }
.order-card.mealwave-online-order { border-color: var(--ods-mealwave) !important; }
.order-card.mealwave-online-order .order-number { color: var(--ods-accent-ink); }
.order-card.ubereats-order { border-color: var(--ods-uber) !important; }
.order-card.ubereats-order .order-number { color: var(--ods-uber); }
</style>

<!-- Non-scoped styles for modal appended to body -->
<style>
.customer-info-modal { position: fixed; inset: 0; background: var(--ods-overlay); z-index: 99999; display: flex; align-items: center; justify-content: center; padding: 20px; }
.customer-info-modal .customer-info-content { background: var(--ods-surface); border: 1px solid var(--ods-line); border-radius: var(--ods-radius-lg); width: 100%; max-width: 420px; max-height: 80vh; overflow: hidden; box-shadow: var(--ods-shadow-lg); color: var(--ods-ink); }
.customer-info-modal .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; background: var(--ods-accent); color: #fff; }
.customer-info-modal .modal-header h3 { font-size: 16px; font-weight: 800; margin: 0; display: flex; align-items: center; }
.customer-info-modal .close-btn { background: rgba(255,255,255,.2); border: none; border-radius: var(--ods-radius-sm); width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #fff; }
.customer-info-modal .modal-body { padding: 16px 18px; overflow-y: auto; max-height: calc(80vh - 60px); }
.customer-info-modal .info-section { background: var(--ods-surface-2); border: 1px solid var(--ods-line); border-radius: var(--ods-radius); padding: 12px 14px; margin-bottom: 12px; }
.customer-info-modal .info-section:last-child { margin-bottom: 0; }
.customer-info-modal .section-title { font-size: 11px; font-weight: 800; color: var(--ods-ink-3); margin-bottom: 8px; display: flex; align-items: center; gap: 8px; text-transform: uppercase; letter-spacing: .07em; }
.customer-info-modal .info-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 0; border-bottom: 1px solid var(--ods-line); gap: 12px; }
.customer-info-modal .info-row:last-child { border-bottom: none; padding-bottom: 0; }
.customer-info-modal .info-row .label { font-size: 13px; color: var(--ods-ink-3); }
.customer-info-modal .info-row .value { font-size: 14px; color: var(--ods-ink); font-weight: 700; }
.customer-info-modal .info-row .value.address { text-align: right; font-size: 13px; line-height: 1.4; }
.customer-info-modal .info-row .phone-link { color: var(--ods-accent-ink); text-decoration: none; }
.customer-info-modal .type-badge, .customer-info-modal .payment-badge { padding: 3px 8px; border-radius: var(--ods-radius-sm); font-size: 12px; font-weight: 700; color: #fff; }
.customer-info-modal .type-badge.delivery { background: var(--ods-prep); }
.customer-info-modal .type-badge.pickup { background: var(--ods-accent); }
.customer-info-modal .payment-badge.cash { background: var(--ods-ok); }
.customer-info-modal .payment-badge.card { background: var(--ods-info); }
</style>
